import { store } from 'redux/store';
import { PermissionTypes } from 'helper/products';
import { selectProductKeys, setProductKey } from 'redux/reducers/productsReducer';
import { productsApi } from 'services/api/products';

/**
 * Re-obtenção da product_key para a recuperação silenciosa do 403 (issue #1841).
 *
 * A chave no Redis do backend rotaciona (TTL 24h) e a cópia persistida aqui
 * fica obsoleta — o backend responde 403 "Acesso Negado". Este módulo re-obtém
 * a chave via `current-user-has-permission` com mutex por produto (N requests
 * simultâneas com a chave velha disparam UMA re-obtenção) e atualiza o slice
 * (o redux-persist regrava o localStorage sozinho).
 *
 * IMPORTANTE: carregado pelo baseQuery via dynamic import — não importar este
 * módulo estaticamente em `services/api/base.tsx` (ciclo base → products → base).
 */

// Janela em que lembramos "chave velha → produto" após uma rotação: cobre a
// corrida da request que ainda sai com a chave antiga depois de o slice já
// ter sido atualizado por outra request.
const ROTATION_MEMORY_MS = 5 * 60 * 1000;

const inFlight = new Map<PermissionTypes, Promise<string | null>>();
const recentRotations = new Map<string, { product: PermissionTypes, at: number }>();

/** Descobre a qual produto pertence a chave que falhou (reverse lookup no slice). */
export function productForStaleKey(staleKey: string): string | null {
  const keys = selectProductKeys(store.getState());
  const products = Object.keys(keys) as PermissionTypes[];
  const direct = products.find((product) => keys[product] === staleKey);
  if (direct) return direct;

  const remembered = recentRotations.get(staleKey);
  if (remembered && Date.now() - remembered.at < ROTATION_MEMORY_MS) {
    return remembered.product;
  }
  return null;
}

/**
 * Garante uma chave atual para o produto: devolve a do slice se já foi
 * renovada por outra request, ou busca no servidor (1 chamada por produto,
 * compartilhada via mutex). Retorna null quando o acesso foi negado (sem
 * contrato/402) ou em falha de rede — o chamador desiste e o fluxo atual
 * (/forbidden) segue valendo.
 */
export async function ensureFreshProductKey(
  product: string,
  staleKey: string | null,
): Promise<string | null> {
  const typedProduct = product as PermissionTypes;

  const current = selectProductKeys(store.getState())[typedProduct];
  if (current && current !== staleKey) return current;

  const pending = inFlight.get(typedProduct);
  if (pending) return pending;

  const promise = (async (): Promise<string | null> => {
    // forceRefetch é OBRIGATÓRIO: o keepUnusedDataFor de 4h devolveria a chave
    // velha do cache RTK. Efeito colateral desejado: o cache é atualizado
    // junto, então usePermissionCheck não regrava a chave velha no slice.
    const subscription = store.dispatch(
      productsApi.endpoints.userHasPermission.initiate(typedProduct, { forceRefetch: true }),
    );
    try {
      const response = await subscription.unwrap();
      const key = response?.data?.product_key;
      if (!key) return null;
      if (staleKey) recentRotations.set(staleKey, { product: typedProduct, at: Date.now() });
      store.dispatch(setProductKey({ product: typedProduct, key }));
      return key;
    } catch {
      return null;
    } finally {
      subscription.unsubscribe();
      inFlight.delete(typedProduct);
    }
  })();

  inFlight.set(typedProduct, promise);
  return promise;
}

/**
 * Chave utilizável para montar URL fora do RTK Query (downloads via axios).
 *
 * Diferente de `getProductKey`, NUNCA devolve a sentinela `__missing__<produto>`:
 * se não houver chave no slice, busca no servidor e, se não conseguir, devolve
 * string vazia (fail-open — o servidor responde o 403 e o chamador decide).
 */
export async function resolveProductKey(product: string): Promise<string> {
  const atual = selectProductKeys(store.getState())[product as PermissionTypes];
  if (atual) return atual;
  const fresh = await ensureFreshProductKey(product, null);
  return fresh ?? '';
}
